<template>
    <form
        class="pin-form"
        :class="{ [customClass]: Boolean(customClass) }"
        autocomplete="off"
        @submit.prevent
    >
        <p class="pin-title">
            <slot name="title">{{ $t('e2ee.enter_pin_code_title') }}</slot>
        </p>
        <div
            v-if="hints"
            class="pin-helper"
            :class="{ ['can-click']: !showHints }"
            @click="showHints = true"
        >
            <span v-show="showHints">{{
                $t('e2ee.hints_tip')
            }}</span>
            {{ showHints ? hints : $t('e2ee.display_hints') }}
        </div>
        <label class="pin-input">
            <input
                v-model="passphrase"
                class="ui-input"
                autocomplete="off"
                :type="inputType"
                autofocus
            />
            <span
                class="input-suffix input-icon can-click"
                :class="{
                    'icon-icon2_eye-close': !showPwd,
                    'icon-icon2_eye-open': showPwd
                }"
                @click="showPwd = !showPwd"
            />
        </label>
        <div class="pin-confirm-button">
            <reo-common-button
                :disabled="disabled"
                :label="$t('e2ee.confirm')"
                @click="verifyPin"
                :data-transaction-name="transactionNames.LEVEL1_ENTER_PIN_CONFIRM"
            />
        </div>
        <div v-show="pinErrorMessage" class="pin-try-error pin-tips red">
            {{ pinErrorMessage }}
        </div>
        <div class="pin-forget-code" v-if="isEnforcedCMK">
            <slot
                name="forgetCode"
                :href="resetUrl"
            >
                <a
                    :href="resetUrl"
                    class="ui-link pin-tips"
                >{{ $t('e2ee.forget_code') }}</a
                >
            </slot>
        </div>
    </form>
</template>
<script lang="ts">
import Vue from 'vue';
import { config } from 'CONFIG';
import { CMKManager, waitInit } from '@/utils/cmk';
import ReoCommonButton from './ReoCommonButton.vue';

/**
 * @type {{
 * succeeded(cb: (task: Promise<any>) => Promise<void>): void;
 * beforeVerify(): void;
 * }}
 */
// export const emits = ['succeeded', 'beforeVerify'];
export default Vue.extend({
    components:{
        ReoCommonButton
    },
    props: {
        /**
         * cmk id
         */
        cid: {
            type: String,
            required: true
        },
        customClass: {
            type: String,
            default: 'default'
        },
        /**
         * 验证所有 followed cmk
         */
        verifyAllFollowed: {
            type: Boolean,
            default: false
        }
    },
    data () {

        return {
            config,
            hints: '',
            isAwaitTask: false,
            isInVerify: false,
            passphrase: '',
            pinErrorMessage: '',
            pinTriedTooMany: false,
            showHints: false,
            showPwd: false,
            usedLockScroll: null
        };

    },
    // emits,
    computed: {
        enforcedCMK(): any {

            return this.$store.getters.enforcedCMK;

        },
        disabled (): boolean {

            return !this.passphrase || this.isInVerify || this.pinTriedTooMany || this.isAwaitTask;

        },
        inputType (): string {

            return this.showPwd ? 'text' : 'password';

        },
        resetUrl (): string {

            return `${config.accountCenterResetPinUrl}&redirect_uri=${encodeURIComponent( this.getHref())}`;

        },
        isEnforcedCMK (): boolean {

            const enforcedCMK = this.enforcedCMK;
            if ( !enforcedCMK ) {

                return false;

            }

            const cmkId = this.cid;
            const enforcedCMKId = enforcedCMK.id;

            if ( enforcedCMKId === cmkId ) {

                return true;

            }

            return CMKManager.isTopFollowCMK( cmkId, enforcedCMKId );

        }
    },
    async created (): Promise<void> {

        const verification = await CMKManager.getCMKVerification( this.cid );
        this.hints = verification?.hints ?? '';

    },
    methods: {
        getHref () {

            return window.location.href;

        },
        async verifyPin () {

            await waitInit();

            this.isInVerify = true;

            try {

                const cmkId = this.cid;
                const pin = this.passphrase;
                this.$emit( 'beforeVerify' );
                this.pinErrorMessage = '';
                if (
                    await CMKManager.verifyCMK(
                        cmkId,
                        pin,
                        this.verifyAllFollowed,
                        ( e ) => {

                            const errorData = e.data;

                            if (( errorData as any )?.loader ) {

                                const errorSymbol =
                                    ( errorData as any ).error?.response?.data?.error
                                        .symbol;

                                if ( errorSymbol ) {

                                    if ( errorSymbol === 'request_throttled' ) {

                                        this.pinErrorMessage = this.$tc(
                                            'e2ee.pin_tried_too_many'
                                        );

                                        this.pinTriedTooMany = true;

                                        return;

                                    }
                                    this.pinErrorMessage = this.$tc(
                                        'e2ee.pin_tried_error'
                                    );

                                    return;

                                }

                            }
                            console.error( e );

                        }
                    )
                ) {

                    CMKManager.Features.recordPinInMemory.set( cmkId, pin );
                    CMKManager.Features.recordPinInStorage.set( cmkId, pin );
                    if ( this.isEnforcedCMK ) {

                        CMKManager.Features.recordPinInStorage.set( this.enforcedCMK.id, pin );

                    }
                    this.$emit( 'succeeded', this.awaitTask.bind( this ));

                }

            } finally {

                this.isInVerify = false;

            }

        },
        async awaitTask ( task ) {

            this.isAwaitTask = true;
            try {

                await task;

            } catch ( e ) {

                if ( e instanceof CMKManager.Error.CMKError ) {

                    this.pinErrorMessage = this.$tc(
                        'errors.error_unknown'
                    );

                } else {

                    this.pinErrorMessage = this.$tc(
                        'errors.server_error'
                    );

                }

                console.error( e );

                throw e;

            } finally {

                this.isAwaitTask = false;

            }

        }
    }
});
</script>
<style lang="less" scoped>
.can-click {
    cursor: pointer;
}
.pin-form {
    width: 100%;
}
.pin-helper {
    color: #555555;
    text-decoration: underline;
    text-decoration-thickness: 1px;
}
.pin-input {
    display: block;
    position: relative;

    .input-suffix {
        position: absolute;
        top: 50%;
        -webkit-transform: translateX(-100%) translateY(-50%);
        transform: translateX(-100%) translateY(-50%);
    }

    & > input {
        width: 100%;
    }

    .ui-input {
        margin-bottom: unset;
    }
}
.default {
    &.pin-form {
        display: flex;
        justify-content: center;
        align-items: center;
        flex-direction: column;
    }
    .pin-title {
        text-align: center;
        font-size: 24px;
    }

    .pin-helper {
        font-size: 16px;
        margin-top: 20px;
    }

    .pin-helper, .pin-input {
        width: 410px;

        @media screen and (max-width: 500px) {
            width: 100%;
        }
    }

    .pin-input {
        margin-top: 40px;

        .ui-input {
            padding-right: 30px;
            @media @mobile {
                margin-bottom: unset;
            }
        }

        .input-suffix {
            font-size: 18px;
            padding: 0 6px;
        }

        & > input {
            padding-left: 16px;
            font-size: 16px;
            height: 48px;
            line-height: 48px;
            font-weight: 500;
        }
    }

    .pin-confirm-button {
        margin: 20px 0;

        .ui-button {
            font-size: 16px;
            line-height: 20px;
            border-radius: 24px;
            padding: 14px 24px;
        }
    }

    .pin-forget-code {
        .ui-link {
            font-size: 16px;
        }
    }

    .pin-try-error {
        margin-bottom: 20px;
    }
}
</style>
